import { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getVisualTypesFromCreativeCategories } from '../util/creativeTypeUtils';

const ServiceErstellenContext = createContext();

const STORAGE_KEY = 'serviceErstellenData';

export const ServiceErstellenProvider = ({ children }) => {
    const [profilePicture, setProfilePicture] = useState(null);

    const user = useSelector((state) => state.auth.user);

    const [serviceData, setServiceData] = useState(() => {
        // Try to load initial data from localStorage
        const savedData = localStorage.getItem(STORAGE_KEY);
        const parsedData = savedData ? JSON.parse(savedData) : null;
        
        // Ensure backward compatibility - add completedSteps if it doesn't exist
        if (parsedData && !parsedData.completedSteps) {
            parsedData.completedSteps = [];
        }
        
        // If no saved data or profilePicture is null, try to use user's profile picture
        const initialProfilePicture = parsedData?.profilePicture || 
            (user?.profilePicture ? { url: user.profilePicture } : null);
        
        // Initialize displayName from user's firstName and lastName if available
        const initialDisplayName = parsedData?.displayName || 
            (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : '');
        
        const normalizedVisualTypes = Array.isArray(parsedData?.visualTypes) && parsedData.visualTypes.length > 0
            ? parsedData.visualTypes
            : getVisualTypesFromCreativeCategories(parsedData?.creativeCategories || parsedData?.creativeType);

        return parsedData ? {
            ...parsedData,
            profilePicture: parsedData.profilePicture || (user?.profilePicture ? { url: user.profilePicture } : null),
            displayName: parsedData.displayName || initialDisplayName,
            // Ensure backward compatibility - add creativeCategories if missing
            creativeCategories: parsedData.creativeCategories || (parsedData.creativeType ? parsedData.creativeType : ''),
            // Ensure creativeType is translated to VISUAL/MUSIC if it's still in old format
            creativeType: parsedData.creativeType === 'MUSIC' || parsedData.creativeType === 'VISUAL' 
                ? parsedData.creativeType 
                : (parsedData.creativeType?.includes('music') ? 'MUSIC' : (parsedData.creativeType ? 'VISUAL' : '')),
            visualTypes: normalizedVisualTypes
        } : {
            completedSteps: [], // Track which steps have been completed
            selectedStudioId: '',
            creativeType: '', // Will be "VISUAL" or "MUSIC"
            creativeCategories: '', // Will store original selection: "music", "foto", "foto,video", etc.
            visualTypes: [],
            profilePicture: initialProfilePicture,
            displayName: initialDisplayName,
            jobTitle: '',
            description: '',
            skills: []
        };
    });

    useEffect(() => {
        if (user?.profilePicture) {
            // If user has a profile picture URL, set it as the initial profile picture
            // This will be a URL string, so we need to convert it to the expected format
            const userProfilePic = user.profilePicture;
            setProfilePicture(userProfilePic);
            
            // Also update serviceData if it's currently null
            setServiceData(prev => {
                if (!prev.profilePicture) {
                    return {
                        ...prev,
                        profilePicture: typeof userProfilePic === 'string' 
                            ? { url: userProfilePic } 
                            : userProfilePic
                    };
                }
                return prev;
            });
        }

        // Initialize displayName from user's firstName and lastName if not already set
        if (user?.firstName && user?.lastName) {
            const fullName = `${user.firstName} ${user.lastName}`.trim();
            setServiceData(prev => {
                // Only set if displayName is empty or not set
                if (!prev.displayName || prev.displayName.trim() === '') {
                    return {
                        ...prev,
                        displayName: fullName
                    };
                }
                return prev;
            });
        }
    }, [user]);

    useEffect(() => {
        try {
            const dataForStorage = {
                ...serviceData,
                profilePicture: serviceData.profilePicture && serviceData.profilePicture !== null 
                    ? (serviceData.profilePicture.file 
                        ? {
                            // If it's a file object, save only metadata (not the file itself)
                            id: serviceData.profilePicture.id,
                            fileName: serviceData.profilePicture.fileName,
                            type: serviceData.profilePicture.type,
                            url: serviceData.profilePicture.url // Save preview URL for display
                        }
                        : serviceData.profilePicture // If it's just a URL object, save as is
                    )
                    : null
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(dataForStorage));
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    }, [serviceData]);

    const updateServiceData = (step, data) => {
        setServiceData(prev => ({
            ...prev,
            [step]: data
        }));
        if (step === 'profilePicture') {
            // If data is an object with url property, extract just the url for profilePicture state
            // Otherwise use the data as is
            setProfilePicture(data?.url || data);
        }
    };

    const markStepCompleted = (stepIndex) => {
        setServiceData(prev => ({
            ...prev,
            completedSteps: prev.completedSteps.includes(stepIndex) 
                ? prev.completedSteps 
                : [...prev.completedSteps, stepIndex]
        }));
    };

    const clearServiceData = () => {
        setServiceData({
            completedSteps: [],
            selectedStudioId: '',
            creativeType: '',
            creativeCategories: '',
            visualTypes: [],
            profilePicture: profilePicture,
            displayName: user?.displayName || '',
            jobTitle: '',
            description: '',
            skills: []
        });
        localStorage.removeItem(STORAGE_KEY);
    };

    return (
        <ServiceErstellenContext.Provider value={{
            serviceData,
            updateServiceData,
            markStepCompleted,
            clearServiceData
        }}>
            {children}
        </ServiceErstellenContext.Provider>
    );
};

export const useServiceErstellen = () => {
    const context = useContext(ServiceErstellenContext);
    if (!context) {
        throw new Error('useServiceErstellen must be used within a ServiceErstellenProvider');
    }
    return context;
};
